<template>
  <Teleport to="body">
    <div
      v-if="open"
      class="estrut-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="estrut-aula-modal-title"
    >
      <div class="estrut-modal estrut-modal--lg">
        <div class="estrut-modal__accent" aria-hidden="true" />

        <header class="estrut-modal__header">
          <div class="estrut-modal__icon estrut-modal__icon--accent">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.75" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.75" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div class="estrut-modal__titles">
            <h2 id="estrut-aula-modal-title" class="estrut-modal__title">
              {{ mode === 'edit' ? t('estruturacao.lessons.editLesson') : t('estruturacao.lessons.newLesson') }}
            </h2>
            <p class="estrut-modal__subtitle">{{ t('estruturacao.lessons.lessonFormHint') }}</p>
          </div>
          <button type="button" class="estrut-modal__close" :aria-label="t('common.close')" @click="close">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </header>

        <form class="estrut-modal__form" @submit.prevent="submit">
          <div class="estrut-modal__body">
            <div v-if="mode === 'create' && lockCategoria && lockedCategoryName" class="estrut-modal__field">
              <span class="estrut-modal__label">{{ t('estruturacao.lessons.categorySelect') }}</span>
              <div class="estrut-modal__category-badge">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                </svg>
                {{ lockedCategoryName }}
              </div>
            </div>

            <div v-else-if="mode === 'create' && !lockCategoria" class="estrut-modal__field">
              <label class="estrut-modal__label" for="estrut-aula-cat">
                {{ t('estruturacao.lessons.categorySelect') }}
                <span class="estrut-modal__required">*</span>
              </label>
              <select id="estrut-aula-cat" v-model="form.categoriaId" class="estrut-modal__input" required>
                <option value="" disabled>{{ t('estruturacao.lessons.categorySelectPlaceholder') }}</option>
                <option v-for="cat in categorias" :key="cat.id" :value="cat.id">{{ cat.titulo }}</option>
              </select>
            </div>

            <div class="estrut-modal__field">
              <label class="estrut-modal__label" for="estrut-aula-titulo">
                {{ t('estruturacao.lessons.lessonTitle') }}
                <span class="estrut-modal__required">*</span>
              </label>
              <input
                id="estrut-aula-titulo"
                v-model="form.titulo"
                type="text"
                class="estrut-modal__input"
                :placeholder="t('estruturacao.lessons.lessonTitlePlaceholder')"
                required
              />
            </div>

            <div class="estrut-modal__field">
              <label class="estrut-modal__label" for="estrut-aula-yt">
                {{ t('estruturacao.lessons.youtubeUrl') }}
                <span class="estrut-modal__optional">({{ t('common.optional') }})</span>
              </label>
              <div class="estrut-modal__input-wrap">
                <span class="estrut-modal__input-icon" aria-hidden="true">
                  <svg fill="currentColor" viewBox="0 0 24 24">
                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                  </svg>
                </span>
                <input
                  id="estrut-aula-yt"
                  v-model="form.youtubeUrl"
                  type="url"
                  class="estrut-modal__input"
                  :placeholder="t('estruturacao.lessons.youtubeUrlPlaceholder')"
                />
              </div>
              <p class="estrut-modal__hint">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {{ t('estruturacao.lessons.youtubeUrlHint') }}
              </p>
            </div>

            <div class="estrut-modal__field">
              <label class="estrut-modal__label" for="estrut-aula-desc">
                {{ t('common.description') }}
                <span class="estrut-modal__optional">({{ t('common.optional') }})</span>
              </label>
              <textarea
                id="estrut-aula-desc"
                v-model="form.descricao"
                rows="3"
                class="estrut-modal__input estrut-modal__textarea"
                :placeholder="t('estruturacao.lessons.lessonDescPlaceholder')"
              />
            </div>

            <label v-if="mode === 'edit'" class="estrut-modal__toggle">
              <input v-model="form.ativo" type="checkbox" />
              <span class="estrut-modal__toggle-track" aria-hidden="true" />
              <span class="estrut-modal__toggle-text">
                <span class="estrut-modal__toggle-label">{{ t('estruturacao.lessons.lessonActive') }}</span>
              </span>
            </label>

            <p v-if="error" class="estrut-modal__error" role="alert">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {{ error }}
            </p>
          </div>

          <footer class="estrut-modal__footer">
            <button type="button" class="estrut-modal__btn estrut-modal__btn--secondary" @click="close">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
              {{ t('common.cancel') }}
            </button>
            <button type="submit" class="estrut-modal__btn estrut-modal__btn--primary" :disabled="saving">
              <svg v-if="saving" class="estrut-modal__btn-spinner" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="3" opacity="0.25" />
                <path fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              <svg v-else-if="mode === 'edit'" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
              </svg>
              <svg v-else fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
              </svg>
              {{ saving ? t('common.loading') : (mode === 'edit' ? t('common.save') : t('common.create')) }}
            </button>
          </footer>
        </form>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { computed, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { isValidLessonVideoUrl, normalizeLessonVideoUrl } from '../../utils/lessonVideoLink'

const props = defineProps({
  open: { type: Boolean, default: false },
  mode: { type: String, default: 'create' },
  categorias: { type: Array, default: () => [] },
  initial: { type: Object, default: null },
  defaultCategoriaId: { type: [Number, String], default: null },
  lockCategoria: { type: Boolean, default: false }
})

const emit = defineEmits(['close', 'submit'])

const { t } = useI18n()

const saving = ref(false)
const error = ref('')

const form = reactive({
  categoriaId: '',
  titulo: '',
  youtubeUrl: '',
  descricao: '',
  ativo: true
})

const lockedCategoryName = computed(() => {
  const id = props.defaultCategoriaId
  if (!id) return ''
  const cat = props.categorias.find((c) => String(c.id) === String(id))
  return cat?.titulo || ''
})

function resetForm() {
  error.value = ''
  saving.value = false
  form.titulo = props.initial?.titulo || ''
  form.youtubeUrl = props.initial?.youtubeUrl || ''
  form.descricao = props.initial?.descricao || ''
  form.ativo = props.initial?.ativo != null ? props.initial.ativo !== false : true
  const catId = props.initial?.categoriaId || props.defaultCategoriaId || props.categorias[0]?.id || ''
  form.categoriaId = catId ? String(catId) : ''
}

watch(
  () => props.open,
  (isOpen, wasOpen) => {
    if (isOpen && !wasOpen) resetForm()
  }
)

function close() {
  emit('close')
}

function submit() {
  const titulo = form.titulo?.trim()
  if (!titulo) {
    error.value = t('estruturacao.lessons.validationTitleRequired')
    return
  }
  const yt = form.youtubeUrl?.trim()
  if (yt && !isValidLessonVideoUrl(yt)) {
    error.value = t('estruturacao.lessons.validationYoutube')
    return
  }
  if (props.mode === 'create' && !form.categoriaId) {
    error.value = t('estruturacao.lessons.validationCategoryRequired')
    return
  }

  error.value = ''
  saving.value = true
  const payload = {
    titulo,
    descricao: form.descricao?.trim() || null,
    ...(props.mode === 'edit' ? { ativo: form.ativo } : {})
  }
  if (yt) {
    payload.youtubeUrl = normalizeLessonVideoUrl(yt) || yt
  } else if (props.mode === 'edit') {
    payload.youtubeUrl = null
  }
  if (props.mode === 'create') payload.categoriaId = Number(form.categoriaId)
  emit('submit', payload)
}

defineExpose({
  setSaving: (v) => { saving.value = v },
  setError: (msg) => { error.value = msg; saving.value = false }
})
</script>

<style src="./estruturacao-modals.css"></style>
